import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/company';
import {
  workspacesApi,
  getWorkspaceId,
  setWorkspaceId,
  type WorkspaceInfo,
} from '../api/workspaces';
import CopilotPanel, { getCopilotStorage, toggleCopilotStorage, type AiAction } from './CopilotPanel';
import CommandPalette from './CommandPalette';
import AIStatusBar from './AIStatusBar';
import AIFloatingBuddy from './AIFloatingBuddy';
import LanguageSwitcher from './LanguageSwitcher';
import { useScreenData, executeAIAction, executeAIFill, scanDOMActions, scanDOMFields, scanDOMTableData, scanActiveRecord, type ScreenData } from './ScreenContext';
import { useModules } from '../hooks/useModules';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { isAdmin } from '../api/client';
import { exceptionsApi } from '../api/aiAuthority';

// Navigation items organized per v2 spec with 12 top-level modules.
// Items with moduleId are only shown when that module is active.
// 'type: section' creates a collapsible group header.
// Label keys are i18n translation keys resolved via useTranslation.
interface NavItem {
  to?: string;
  labelKey: string;
  icon: string;
  moduleId?: string;
  adminOnly?: boolean;
  type?: string;
  feature?: string;
}

const NAV_ITEMS: NavItem[] = [
  // 1. Dashboard
  { to: '/', labelKey: 'nav.dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1' },

  // 1b. Analytics — drill-down dashboard (feature-flagged, egress-free)
  { to: '/analytics', labelKey: 'nav.analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', feature: 'analytics' },

  // 2. Sales
  { to: '/invoices/made', labelKey: 'nav.sales', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },

  // 3. Purchases
  { to: '/invoices/received', labelKey: 'nav.purchases', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z' },

  // 4. Banking & Cash
  { to: '/banking', labelKey: 'nav.bankingCash', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3' },

  // 5. Journal
  { to: '/journal', labelKey: 'nav.journal', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },

  // 6. Assets (module-gated)
  { to: '/fixed-assets', labelKey: 'nav.assets', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', moduleId: 'Assets' },

  // 7. Reports
  { to: '/reports/vat', labelKey: 'nav.reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },

  // 8. Tax & Compliance
  { to: '/reports/saft', labelKey: 'nav.taxCompliance', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z' },

  // 9. Close
  { to: '/periods/close', labelKey: 'nav.close', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },

  // 10. Setup (group header with sub-items)
  { labelKey: 'nav.setup', icon: 'M4 6h16M4 10h16M4 14h16M4 18h16', type: 'section' },
  { to: '/accounts', labelKey: 'nav.accounts', icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { to: '/persons', labelKey: 'nav.contacts', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { to: '/catalog', labelKey: 'nav.catalog', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },

  // 11. AI & Audit — admin only (operator diagnostics; AI data egress is per-workspace gated)
  { to: '/ai-audit', labelKey: 'nav.aiAudit', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', adminOnly: true },

  // 11b. Arena — admin only (agent diagnostics surface)
  { to: '/arena', labelKey: 'nav.arena', icon: 'M13 10V3L4 14h7v7l9-11h-7z', adminOnly: true },

  // 12. Settings
  { to: '/settings', labelKey: 'nav.settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

// Map route paths to human-readable context labels
const PATH_CONTEXT: Record<string, string> = {
  '/': 'Dashboard',
  '/analytics': 'Analitika (Analytics)',
  '/invoices': 'Invoices',
  '/invoices/made': 'Pardavimu saskaitos (Sales Invoices)',
  '/invoices/received': 'Pirkimu saskaitos (Purchase Invoices)',
  '/invoices/new': 'Nauja saskaita faktura (Creating Invoice)',
  '/invoices/credit-note/new': 'Kreditine saskaita faktura (Credit Note)',
  '/banking': 'Bankines operacijos (Banking & Cash)',
  '/banking/accounts': 'Banko saskaitos (Bank Accounts)',
  '/banking/import': 'Banko isklotines importas (Bank Import)',
  '/cash-operations': 'Kasos operacijos (Cash Operations)',
  '/offsets': 'Uzskaitos (Offsets)',
  '/advance-reports': 'Avansines ataskaitos (Advance Reports)',
  '/journal': 'Zurnalas (Journal Entries)',
  '/journal/new': 'Naujas zurasklo irasas (New Journal Entry)',
  '/accounts': 'Saskaitu planas (Chart of Accounts)',
  '/accounts/trial-balance': 'Bandomasis balansas (Trial Balance)',
  '/persons': 'Kontaktai (Contacts)',
  '/catalog': 'Prekiu ir paslaugu katalogas (Catalog)',
  '/fixed-assets': 'Ilgalaikis turtas (Fixed Assets)',
  '/inventory': 'Atsargos (Inventory)',
  '/payroll': 'Darbo uzmokestis (Payroll)',
  '/reports/vat': 'PVM ataskaita (VAT Summary)',
  '/reports/vat-declaration': 'PVM deklaracija (VAT Declaration)',
  '/reports/financial-statements': 'Finansines ataskaitos (Financial Statements)',
  '/reports/debt-aging': 'Skolu senumo ataskaita (Debt Aging)',
  '/reports/saft': 'Mokesciai ir atitiktis (Tax & Compliance)',
  '/periods/close': 'Periodo uzdarymas (Period Close)',
  '/audit': 'Audito zurasklas (Audit Trail)',
  '/ai-audit': 'AI ir Auditas (AI & Audit)',
  '/arena': 'Agentu Arena (Agent Arena)',
  '/settings': 'Nustatymai (Settings)',
  '/settings/roles': 'Vaidmenys (User Roles)',
  '/settings/backup': 'Atsargine kopija (Data Backup)',
  '/settings/email': 'El. pasto konfiguracija (Email Config)',
  '/workspaces': 'Darbo sritys (Workspaces)',
};

function getPageContext(pathname: string): string {
  // Exact match first
  if (PATH_CONTEXT[pathname]) return PATH_CONTEXT[pathname];
  // Check for dynamic routes
  if (pathname.startsWith('/invoices/') && pathname.includes('/edit')) return 'Redaguoja saskaita fakture (Editing Invoice)';
  if (pathname.startsWith('/journal/') && pathname.endsWith('/edit')) return 'Redaguoja zurasklo irasa (Editing Journal Entry)';
  if (pathname.startsWith('/invoices/')) return 'Ziuri saskaita fakture (Viewing Invoice)';
  if (pathname.startsWith('/journal/')) return 'Ziuri zurasklo irasa (Viewing Journal Entry)';
  // Fallback
  return pathname;
}

export default function Layout({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceDropdown, setWorkspaceDropdown] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(getCopilotStorage);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screenData = useScreenData();
  const { isModuleActive } = useModules();
  const { enableAiBuddy, enableAnalytics } = useFeatureFlags();
  const admin = isAdmin();

  // Filter nav items: show core items always, module items only when active,
  // admin-only items (Arena, AI & Audit) only for Admins, feature-flagged
  // items only when their flag is on.
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (item.adminOnly && !admin) return false;
    if (item.feature === 'analytics' && !enableAnalytics) return false;
    if (!item.moduleId) return true; // Core item
    return isModuleActive(item.moduleId);
  });

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });

  const activeWorkspace = workspaces?.find((w: WorkspaceInfo) => w.isActive);
  // Sync: ensure workspace ID is in localStorage before child components render
  if (activeWorkspace && !getWorkspaceId()) {
    setWorkspaceId(activeWorkspace.id);
  }
  const hasWorkspace = !!getWorkspaceId();

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    enabled: hasWorkspace,
  });

  // Exceptions count for badge on AI & Audit nav item
  const { data: exceptionsCount } = useQuery<number>({
    queryKey: ['exceptions-count'],
    queryFn: () => exceptionsApi.getCount(),
    enabled: hasWorkspace,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const handleSwitchWorkspace = async (workspace: WorkspaceInfo) => {
    try {
      await workspacesApi.activate(workspace.id);
      setWorkspaceId(workspace.id);
      setWorkspaceDropdown(false);
      // Invalidate all queries to refetch with new workspace
      queryClient.invalidateQueries();
    } catch (err) {
      // Silent failures here once cost an hour of debugging (403 from an
      // admin-gated activate endpoint) — always surface the reason.
      alert(err instanceof Error ? `Nepavyko perjungti darbo srities: ${err.message}` : 'Nepavyko perjungti darbo srities');
    }
  };

  // Initialize workspace ID on mount
  useEffect(() => {
    if (activeWorkspace && !getWorkspaceId()) {
      setWorkspaceId(activeWorkspace.id);
    }
  }, [activeWorkspace]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K or Cmd+K: open command palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
      // Escape: close command palette (copilot handles its own escape)
      if (e.key === 'Escape' && commandPaletteOpen) {
        setCommandPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commandPaletteOpen]);

  // Listen for external copilot-open events (e.g., from arena sendCopilotMessage)
  useEffect(() => {
    const handler = () => {
      setCopilotOpen(true);
      toggleCopilotStorage(true);
    };
    window.addEventListener('copilot-open', handler);
    return () => window.removeEventListener('copilot-open', handler);
  }, []);

  const toggleCopilot = useCallback(() => {
    setCopilotOpen(prev => {
      const next = !prev;
      toggleCopilotStorage(next);
      return next;
    });
  }, []);

  const handleCopilotClose = useCallback(() => {
    setCopilotOpen(false);
    toggleCopilotStorage(false);
  }, []);

  const handleStatusBarClick = useCallback(() => {
    navigate('/ai-audit');
  }, [navigate]);

  const handleAiAction = useCallback((action: AiAction) => {
    // Direct workspace activation — bypasses dropdown entirely
    if (action.actionType === 'activate_workspace' && action.target) {
      const target = action.target.trim().toLowerCase();
      const ws = workspaces?.find((w: WorkspaceInfo) =>
        w.name.toLowerCase() === target || String(w.id) === action.target
      );
      if (ws) {
        handleSwitchWorkspace(ws);
      } else {
        console.warn('AI activate_workspace: workspace not found:', action.target, 'Available:', workspaces?.map((w: WorkspaceInfo) => w.name));
      }
      return;
    }
    if (action.actionType === 'ui_click' && action.target) {
      executeAIAction(action.target);
      return;
    }
    if (action.actionType === 'ui_fill' && action.target) {
      executeAIFill(action.target, action.value ?? '');
      return;
    }
    // navigate with optional prefill
    const path = action.path;
    if (!path || !path.startsWith('/') || path.includes('//') || path.includes(':')) {
      console.warn('AI action rejected: invalid path', path);
      return;
    }
    if (action.formData) {
      navigate(path, { state: { aiPrefill: { ...action.formData, explanation: action.reason } } });
    } else {
      navigate(path);
    }
  }, [navigate]);

  const pageContextLabel = getPageContext(location.pathname);
  const pageContext = [
    pageContextLabel,
    company ? `${t('layout.company')}: ${company.name}` : '',
    activeWorkspace?.name ? `${t('layout.area')}: ${activeWorkspace.name}` : '',
  ].filter(Boolean).join(' | ');

  // Build structured screen data for the AI copilot (sent as screenData, served via get_screen_state tool).
  // Scans DOM for all interactive elements (explicit data-ai-* attrs + semantic HTML), merges with page-reported data.
  // Also scans visible table data and active/selected records for AI context.
  // NEVER returns null -- always provides at least the page context so the AI knows where the user is.
  const buildScreenData = (): ScreenData => {
    const domActions = scanDOMActions();
    const domFields = scanDOMFields();
    const domTableData = scanDOMTableData();
    const domActiveRecord = scanActiveRecord();
    const hasAnyData = screenData.data || domActions.length > 0 || domFields.length > 0;
    const base = hasAnyData ? (screenData.data || { summary: '' }) : { summary: pageContextLabel };
    return {
      summary: base.summary || pageContextLabel,
      recordCount: base.recordCount,
      filters: base.filters,
      preview: base.preview,
      tableData: base.tableData && base.tableData.length > 0 ? base.tableData : (domTableData.length > 0 ? domTableData : undefined),
      activeRecord: base.activeRecord || domActiveRecord || undefined,
      formFields: [
        ...(base.formFields || []),
        ...domFields.filter(df => !(base.formFields || []).some(pf => pf.name === df.name)),
      ],
      updatedAt: base.updatedAt,
      actions: [
        ...(base.actions || []),
        ...domActions.filter(da => !(base.actions || []).some(pa => pa.id === da.id)),
      ],
      // Expose workspace list so AI can switch workspaces directly via activate_workspace action
      workspaces: workspaces?.map((ws: WorkspaceInfo) => ({
        id: ws.id,
        name: ws.name,
        isActive: ws.isActive,
        companyName: ws.companyName,
      })) ?? [],
      activeWorkspace: activeWorkspace ? { id: activeWorkspace.id, name: activeWorkspace.name } : null,
    };
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary-100 text-primary-800'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-30 w-56 bg-white border-r border-gray-200 transform transition-transform lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo / Company */}
          <div className="px-4 py-3 border-b border-gray-200">
            <h1 className="mb-0">
              <img src="/logo.png" alt="A5Suite" className="h-8" />
            </h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item, idx) => {
              // Section headers (no navigation)
              if (item.type === 'section') {
                return (
                  <div key={`section-${idx}`} className="pt-3 pb-1 px-3">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t(item.labelKey)}</span>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.to}
                  to={item.to!}
                  end={item.to === '/'}
                  className={linkClass}
                  onClick={() => setSidebarOpen(false)}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                  </svg>
                  <span className="flex-1">{t(item.labelKey)}</span>
                  {/* Admin-only marker */}
                  {item.adminOnly && (
                    <span className="flex-shrink-0 text-gray-400" title={t('nav.adminOnly')}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                    </span>
                  )}
                  {/* Exceptions badge on AI & Audit */}
                  {item.to === '/ai-audit' && exceptionsCount && exceptionsCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {exceptionsCount > 99 ? '99+' : exceptionsCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-400">
            A5Suite {__APP_VERSION__}
          </div>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="flex items-center gap-4 px-4 py-2 bg-white border-b border-gray-200">
          <button
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Workspace Selector */}
          <div className="relative">
            <button
              data-ai-action="workspace-dropdown"
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-gray-50 transition-colors"
              onClick={() => setWorkspaceDropdown(!workspaceDropdown)}
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <span className="max-w-[150px] truncate font-medium">
                {activeWorkspace?.name ?? t('layout.noWorkspace')}
              </span>
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {workspaceDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setWorkspaceDropdown(false)} />
                <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase">{t('layout.workspaces')}</p>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {workspaces?.map((ws: WorkspaceInfo) => (
                      <button
                        key={ws.id}
                        data-ai-action={`workspace-${ws.name.toLowerCase().replace(/\s+/g, '_')}`}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          ws.isActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                        }`}
                        onClick={() => handleSwitchWorkspace(ws)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{ws.name}</div>
                        </div>
                        {ws.isActive && (
                          <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                    {(!workspaces || workspaces.length === 0) && (
                      <div className="px-3 py-4 text-sm text-gray-400 text-center">{t('workspace.noWorkspaces')}</div>
                    )}
                  </div>
                  {/* Operator-only: workspace/company management (import client db,
                      create new company). */}
                  {admin && (
                    <div className="border-t border-gray-100 px-3 py-2 flex gap-2">
                      <button
                        className="flex-1 text-xs text-primary-600 hover:text-primary-800 font-medium py-1"
                        onClick={() => {
                          setWorkspaceDropdown(false);
                          navigate('/workspaces');
                        }}
                      >
                        {t('layout.manageWorkspaces')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Command palette trigger */}
          <button
            onClick={() => setCommandPaletteOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
            title={t('layout.searchCtrlK')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline">{t('common.search')}...</span>
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white rounded border border-gray-200">
              Ctrl+K
            </kbd>
          </button>

          <div className="flex-1" />

          {/* Copilot toggle — desktop only; mobile uses FAB below. Hidden unless AI is feature-flagged on. */}
          {enableAiBuddy && (
            <button
              onClick={toggleCopilot}
              className={`hidden sm:flex p-2 rounded-lg transition-colors ${
                copilotOpen
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              }`}
              title="AI Copilot"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
          )}

          {/* Page context indicator */}
          <span className="text-sm text-gray-500 hidden md:inline">{pageContextLabel}</span>

          {/* Language Switcher */}
          <LanguageSwitcher />

          <button
            onClick={onLogout}
            className="ml-2 px-3 py-1 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title={t('auth.signOut')}
          >
            {t('auth.signOut')}
          </button>
        </header>

        {/* Content area with optional copilot */}
        <div className="flex-1 flex overflow-hidden">
          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-4 lg:p-6">
            <Outlet />
          </main>

          {/* Copilot panel — only when AI is feature-flagged on */}
          {enableAiBuddy && (
            <CopilotPanel
              open={copilotOpen}
              onClose={handleCopilotClose}
              pageContext={`${location.pathname} - ${pageContext}`}
              screenData={buildScreenData()}
              onAction={handleAiAction}
            />
          )}
        </div>

        {/* AI Status Bar — only when AI is feature-flagged on; admin-only (its only
            affordance opens /ai-audit; the same data lives on the Dashboard tiles) */}
        {enableAiBuddy && admin && <AIStatusBar onClick={handleStatusBarClick} />}
      </div>

      {/* Command Palette (global overlay) */}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />

      {/* AI Floating Buddy — proactive AI assistant on every page; feature-flagged */}
      {enableAiBuddy && !copilotOpen && (
        <AIFloatingBuddy
          currentPath={location.pathname}
          pageContextLabel={pageContextLabel}
          screenData={buildScreenData()}
          onOpenCopilot={toggleCopilot}
          exceptionsCount={exceptionsCount}
        />
      )}
    </div>
  );
}
